/**
 * ActivateConfirmDialog — shared confirmation modal for activate AND rollback
 * (Story 7.6 AC2.c / AC2.d).
 *
 * Activate vs. Rollback is purely a UX nuance — the backend operation is the
 * same POST /admin/prompts/:id/versions/:versionId/activate. We change the
 * wording + iconography so the operator knows which direction they are moving.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export type ActivateMode = 'activate' | 'rollback';

export interface ActivateConfirmDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  mode: ActivateMode;
  versionNumber: number;
  previousActiveVersionNumber: number | null;
  onConfirm: () => void;
  pending?: boolean;
}

export function ActivateConfirmDialog({
  open,
  onOpenChange,
  mode,
  versionNumber,
  previousActiveVersionNumber,
  onConfirm,
  pending,
}: ActivateConfirmDialogProps) {
  const isRollback = mode === 'rollback';

  const title = isRollback ? `Reverter para v${versionNumber}?` : `Ativar v${versionNumber}?`;

  const description = isRollback
    ? `Esta versão é mais antiga que a ativa (v${previousActiveVersionNumber ?? '?'}). Conversas em andamento não serão afetadas (usam snapshot). Novas conversas usarão v${versionNumber}.`
    : `A versão v${previousActiveVersionNumber ?? '—'} será desativada. Conversas em andamento continuam na versão antiga (snapshot). Novas conversas usarão v${versionNumber}.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant={isRollback ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={pending}
          >
            {pending
              ? 'Aplicando…'
              : isRollback
                ? `Reverter para v${versionNumber}`
                : `Ativar v${versionNumber}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ActivateConfirmDialog;
